import { Row, RowData, TableMeta } from "@tanstack/react-table";
import {
  MouseEventHandler,
  MouseEvent,
  useMemo,
  useRef,
  useCallback,
  MutableRefObject,
  Dispatch,
  SetStateAction,
} from "react";
import { SELECTABLE_ROW_STYLES } from "utils/chakraHelpers";
import { ComponentProps } from ".";

export type SelectableRowProps = {
  rowId: string | undefined;
  handleChange: () => void;
  handleRowClick: MouseEventHandler<HTMLTableRowElement>;
  isDisabled: boolean | undefined;
  isSelected: boolean | undefined;
};

type RowClickHandlerFunction<D extends RowData> = (
  props: Omit<SelectableRowProps, "handleRowClick">,
  row: Row<D>,
  event: MouseEvent<HTMLTableRowElement>,
) => void;

type RowContextOptions<D extends RowData> = {
  getIdForRow?: (row: Row<D>) => string | undefined;
  getIsRowDisabled?: (row: Row<D>) => boolean;
  handleRowClick?: RowClickHandlerFunction<D>;
};

export type SelectSingleOptions = {
  isMulti?: false;
  onSelectedChange: (id: string) => void;
  onSelectedStateChange?: never;
  selected?: string | null;
};

export type SelectMultiOptions = {
  isAllSelected?: boolean;
  isMulti: true;
  onSelectedChange: Dispatch<SetStateAction<string[]>>;
  selected?: string[] | null;
};

export type SelectMultiOrAllOptions = {
  isMulti: true;
  onSelectedChange: Dispatch<SetStateAction<string[] | true>>;
  selected?: string[] | true | null;
};

export type SelectStateOptions = SelectSingleOptions | SelectMultiOptions;

function defaultGetIdForRow<D extends RowData>(row: Row<D>) {
  return (row.original as { id: string }).id;
}

type Options<D extends RowData> = RowContextOptions<D> & SelectStateOptions;

export function getSelectableRowProps<D extends RowData>(
  row: Row<D>,
  options: Options<D>,
): SelectableRowProps {
  const {
    isMulti,
    selected,
    onSelectedChange,
    getIdForRow = defaultGetIdForRow,
    getIsRowDisabled,
    handleRowClick: handleRowClickOpt,
  } = options;

  const rowId = getIdForRow(row);

  const isDisabled = getIsRowDisabled?.(row);

  const isSelected = isMulti
    ? options.isAllSelected || (!!rowId && selected?.includes(rowId))
    : selected === rowId;

  const handleChange = () => {
    if (!rowId || isDisabled) return;
    if (isMulti) {
      onSelectedChange((existing) => {
        const newSelected = existing?.slice(0) || [];
        const existingIdx = newSelected.indexOf(rowId);
        if (existingIdx >= 0) {
          newSelected.splice(existingIdx, 1);
        } else {
          newSelected.push(rowId);
        }
        return newSelected;
      });
    } else {
      onSelectedChange(rowId);
    }
  };

  const baseProps = { rowId, handleChange, isDisabled, isSelected };

  const handleRowClick: MouseEventHandler<HTMLTableRowElement> = (evt) => {
    if (handleRowClickOpt) handleRowClickOpt(baseProps, row, evt);
    else handleChange();
  };

  return { ...baseProps, handleRowClick };
}

export function getCheckboxSelectableRowProps<D extends RowData>(
  row: Row<D>,
  meta: SelectableRowTableMeta<D>,
) {
  const { checkboxRefsById, ...rest } = meta;
  const baseProps = getSelectableRowProps(row, rest);

  let inputRef: CheckboxInputRef | undefined;
  if (baseProps.rowId) {
    if (!checkboxRefsById[baseProps.rowId])
      checkboxRefsById[baseProps.rowId] = { current: null };
    inputRef = checkboxRefsById[baseProps.rowId];
  }

  return {
    ...baseProps,
    inputRef,
  };
}

export default function useSelectableRow<D extends RowData>(
  options: Options<D>,
): Partial<ComponentProps<D>> {
  return {
    getRowProps: (row) => {
      const { isDisabled, isSelected, handleRowClick } = getSelectableRowProps(
        row,
        options,
      );
      return {
        ...(isDisabled ? {} : SELECTABLE_ROW_STYLES),
        "aria-selected": isSelected,
        onClick: handleRowClick,
      };
    },
  };
}

export type CheckboxRefElement = { click: () => void };

export type CheckboxInputRef = MutableRefObject<HTMLInputElement | null>;

type CheckboxRefsById = {
  [id: string]: CheckboxInputRef;
};

export type SelectableRowTableMeta<D extends RowData> = TableMeta<D> &
  Options<D> & {
    checkboxRefsById: CheckboxRefsById;
  };

function getTargetIsWithinRef(
  target: HTMLElement | null | undefined,
  ref: HTMLElement | null,
) {
  let i = 0;

  let currentNode: HTMLElement | null = target?.parentElement || null;

  while (i < 3 && currentNode && currentNode !== ref) {
    i += 1;
    currentNode = currentNode.parentElement;
  }

  return currentNode === ref;
}

export function useTableCheckboxSelection<D extends RowData>({
  isMulti,
  getIdForRow = defaultGetIdForRow,
  getIsRowDisabled,
  onSelectedChange,
  selected,
}: Options<D>) {
  const checkboxRefsById = useRef<CheckboxRefsById>({});

  const handleRowClick = useCallback<RowClickHandlerFunction<D>>(
    ({ rowId, handleChange }, row, event) => {
      if (!rowId) return;
      const inputRef = checkboxRefsById.current[rowId];
      const inputEl = inputRef.current;

      if (!inputEl) return handleChange();

      if (
        getTargetIsWithinRef(
          event.target as HTMLElement | null,
          inputEl.parentElement,
        )
      )
        return;

      inputEl.click();
      inputEl.focus();
    },
    [checkboxRefsById],
  );

  const tableOptions = useMemo<Options<D>>(
    () => ({
      getIdForRow,
      getIsRowDisabled,
      handleRowClick,
      ...(isMulti
        ? { isMulti: true, onSelectedChange, selected }
        : { isMulti: false, onSelectedChange, selected }),
    }),
    [
      isMulti,
      getIdForRow,
      getIsRowDisabled,
      onSelectedChange,
      selected,
      handleRowClick,
    ],
  );

  const selectableRowTableMeta = useMemo<SelectableRowTableMeta<D>>(
    () => ({
      ...tableOptions,
      checkboxRefsById: checkboxRefsById.current,
    }),
    [checkboxRefsById, tableOptions],
  );

  const selectableRowTableProps = useSelectableRow(tableOptions);

  return { selectableRowTableMeta, selectableRowTableProps };
}
